import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import EToast from '@/components/everridge/EToast';
import { CreateFaqDto, FaqDetailsResponseDto, UpdateFaqDto } from '@/features/faq/types/faq-dto';
import { t } from 'i18next';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {} & ApiHookProps;

export const useFaqDetailsApi = ({ id }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();

    const {
        data: faqDetailsData, 
        isLoading: isLoadingFaqDetailsData, 
        isError: isErrorFaqDetailsData
    } = useQuery({
        queryKey: [ApiConfig.queries.features.faq.queryKeys.details, id],
        queryFn: async (): Promise<FaqDetailsResponseDto> => {
            const url = `${ApiConfig.queries.features.faq.endpoints.get}/${id}`;               
            const response = await axiosInstance.get<FaqDetailsResponseDto>(url);
            return response.data;
        },
        enabled: !!id,        
    });

    const {
        mutateAsync: createFaq, 
        isPending: isCreatingFaq, 
        isError: isErrorCreatingFaq
    } = useMutation({
        mutationFn: async (dto: CreateFaqDto) => {
            const response = await axiosInstance.post(
                ApiConfig.queries.features.faq.endpoints.create, dto
            ); 
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ApiConfig.queries.features.faq.queryKeys.list] 
            });
            EToast({
                variant: 'success',
                message: t('main.faq.toasts.create.success.message'), 
                description: t('main.faq.toasts.create.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.faq.toasts.create.error.message'), 
                description: t('main.faq.toasts.create.error.description')
            });
        }
    });
    
    const {
        mutateAsync: updateFaq, 
        isPending: isUpdatingFaq, 
        isError: isErrorUpdatingFaq
    } = useMutation({
        mutationFn: async (dto: UpdateFaqDto) => {
            const url = `${ApiConfig.queries.features.faq.endpoints.update}/${id}`; 
            const response = await axiosInstance.put(url, dto);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ApiConfig.queries.features.faq.queryKeys.list]
            });
            EToast({
                variant: 'success',
                message: t('main.faq.toasts.update.success.message'), 
                description: t('main.faq.toasts.update.success.description')
            });  
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.faq.toasts.update.error.message'), 
                description: t('main.faq.toasts.update.error.description')
            });
        }
    });

    const {
        mutateAsync: deleteFaq, 
        isPending: isDeletingFaq, 
        isError: isErrorDeletingFaq
    } = useMutation({
        mutationFn: async (id: string) => {
            const url = `${ApiConfig.queries.features.faq.endpoints.delete}/${id}`;
            const response = await axiosInstance.delete(url);       
            return response.data;            
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.faq.queryKeys.list]
            });
            EToast({
                variant: 'success', 
                message: t('main.faq.toasts.delete.success.message'), 
                description: t('main.faq.toasts.delete.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error', 
                message: t('main.faq.toasts.delete.error.message'), 
                description: t('main.faq.toasts.delete.error.description')
            });
        }
    });

    const {
        mutateAsync: updateFaqIndex, 
        isPending: isUpdatingFaqIndex, 
        isError: isErrorUpdatingFaqIndex
    } = useMutation({
        mutationFn: async ({ id, newIndex }: {id: string, newIndex: number}) => {
            const url = `${ApiConfig.queries.features.faq.endpoints.updateIndex}/${id}/UpdateIndex`;
            const response = await axiosInstance.put(url, newIndex);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ApiConfig.queries.features.faq.queryKeys.list]
            });           
        },
        onError: () => {}
    });

    return {  
        faqDetailsData, 
        isLoadingFaqDetailsData, 
        isErrorFaqDetailsData,
        createFaq, 
        isCreatingFaq, 
        isErrorCreatingFaq,
        updateFaq, 
        isUpdatingFaq, 
        isErrorUpdatingFaq,
        deleteFaq,
        isDeletingFaq,
        isErrorDeletingFaq,
        updateFaqIndex,
        isUpdatingFaqIndex,
        isErrorUpdatingFaqIndex
    }
};
